/**
 * SP2 §8 (Task 11) — read-only transaction panel on batch detail
 * (remittance surface only). The ONLY action is the supervisor balance
 * override; all other repair is SP3. Renders after ReconciliationPanel and
 * follows its visual conventions (shared batches.css, "txn-*" classes
 * alongside its "recon-*" classes, same status-token usage).
 */
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOverrideTransactionBalance, type Transaction } from "../hooks/useBatches";
import { canOverrideTransactionBalance } from "../lib/permissions";
import "../batches.css";

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

interface TransactionsPanelProps {
  batchId: string;
  /** SP2 §9 — BatchDetail.surface; "standard" batches render nothing. */
  surface: string;
  transactions: Transaction[];
  /** Called after a successful override so the caller can refetch batch detail. */
  onChanged: () => void;
}

export function TransactionsPanel({ batchId, surface, transactions, onChanged }: TransactionsPanelProps) {
  const { user } = useAuth();
  const canOverride = canOverrideTransactionBalance(user);
  const { override, isSubmitting } = useOverrideTransactionBalance();
  const [busyId, setBusyId] = useState<string | null>(null);

  // Regression pin (Task 9) — standard batches always carry
  // surface:"standard" and transactions:[], so this also covers the "zero
  // transactions" case without a second condition.
  if (surface !== "remittance" || transactions.length === 0) return null;

  const handleOverride = async (txn: Transaction) => {
    const reason = window.prompt(
      `Override the balance failure on Transaction ${txn.ordinal}? Enter a reason:`,
    );
    if (!reason?.trim()) return;
    setBusyId(txn.id);
    try {
      await override(batchId, txn.id, reason.trim());
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="txn-panel" data-testid="transactions-panel">
      <div className="txn-title">Transactions</div>
      {transactions.map((txn) => {
        const snapshot = txn.balanceSnapshot;
        // Overridden takes precedence over the raw verdict — the failure
        // treatment (red chip, Override button) is replaced by the marker.
        const verdict = txn.overriddenAt ? "overridden" : (snapshot?.verdict ?? "indeterminate");

        return (
          <div key={txn.id} className="txn-row" data-testid={`txn-row-${txn.id}`}>
            <div className="txn-row-header">
              <span className="txn-ordinal">#{txn.ordinal}</span>
              <span className="txn-state">{txn.state}</span>
              <span className={`txn-verdict txn-verdict--${verdict}`}>{verdict}</span>
            </div>

            {snapshot && (
              <div className="txn-amounts">
                <span>Σ payment {formatCurrency(snapshot.paymentTotalCents)}</span>
                <span>Σ applied {formatCurrency(snapshot.appliedTotalCents)}</span>
                <span>Δ {formatCurrency(snapshot.deltaCents)}</span>
                <span className="txn-source">
                  {snapshot.source === "ocr" ? "provisional · OCR" : "keyed"}
                </span>
              </div>
            )}

            <div className="txn-docs">
              {txn.documents.map((doc) => (
                <span key={doc.id} className="txn-doc-chip" title={doc.filename}>
                  {doc.classification ?? "unclassified"} · {doc.displayId ?? doc.filename}
                </span>
              ))}
            </div>

            {txn.overriddenAt ? (
              <span className="txn-override-note" title={txn.overrideReason ?? ""}>
                Override recorded
              </span>
            ) : canOverride && verdict !== "balanced" ? (
              <button
                type="button"
                className="batch-action-btn"
                disabled={isSubmitting && busyId === txn.id}
                aria-label={`Override Transaction ${txn.ordinal}`}
                onClick={() => void handleOverride(txn)}
              >
                Override
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
